import { useEffect } from 'react';
import { NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { BEREICH_NAMEN, stufeMindestens, type Aktivitaet, type MeinProjekt } from '@beholder/shared';
import { api } from '../api';
import { WEB_MODULE, type ModulSeiteProps, type WebModul } from '../modul';
import { useIch } from '../sitzung';
import { Symbol } from '../symbole';
import { Karte, Laden, Leer, Meldung, datumZeit, useDaten } from '../ui';
import { Mitglieder } from './Mitglieder';
import { ProjektEinstellungen } from './ProjektEinstellungen';
import { Papierkorb } from './Papierkorb';

export function ProjektRahmen() {
  const { projektId = '' } = useParams();
  const ich = useIch();
  const { daten: projekt, fehler, neuLaden } = useDaten(() => api.get<MeinProjekt>(`/api/projekte/${projektId}`), [projektId]);

  // Wenn sich die eigenen Rechte ändern (Live-Hinweis), Projekt neu laden.
  useEffect(() => {
    const h = () => void neuLaden();
    window.addEventListener('beholder:rechte', h);
    return () => window.removeEventListener('beholder:rechte', h);
  }, [neuLaden]);

  if (fehler) return <div className="seite"><Meldung>{fehler}</Meldung></div>;
  if (!projekt) return <Laden />;

  const sichtbar = WEB_MODULE.filter((m) => stufeMindestens(projekt.rechte[m.id], 'lesen'));
  const props = (id: (typeof WEB_MODULE)[number]['id']): ModulSeiteProps => ({
    projekt,
    ich,
    stufe: projekt.rechte[id],
    darfSchreiben: projekt.rechte[id] === 'schreiben' && !projekt.archiviert,
    neuLaden: () => void neuLaden(),
  });

  return (
    <div className="projekt">
      <header className="projekt-kopf">
        <div className="projekt-titel">
          <h1>{projekt.name}</h1>
          {projekt.archiviert && <span className="marke marke-warn">archiviert – nur lesen</span>}
        </div>
        <nav className="reiter" aria-label="Bereiche">
          <NavLink end to={`/p/${projekt.id}`}>
            {Symbol.uebersicht} <span>Übersicht</span>
          </NavLink>
          {sichtbar.filter((m) => m.Seite).map((m) => (
            <NavLink key={m.id} to={`/p/${projekt.id}/${m.id}`}>
              {m.symbol} <span>{m.name}</span>
            </NavLink>
          ))}
          <NavLink to={`/p/${projekt.id}/mitglieder`}>
            {Symbol.mitglieder} <span>Mitglieder</span>
          </NavLink>
          <NavLink to={`/p/${projekt.id}/papierkorb`}>
            {Symbol.muell} <span>Papierkorb</span>
          </NavLink>
          {projekt.verwalten && (
            <NavLink to={`/p/${projekt.id}/einstellungen`}>
              {Symbol.einstellungen} <span>Einstellungen</span>
            </NavLink>
          )}
        </nav>
      </header>
      <main className="projekt-inhalt">
        <Routes>
          <Route index element={<Uebersicht projekt={projekt} sichtbar={sichtbar} props={props} />} />
          <Route path="mitglieder" element={<Mitglieder projekt={projekt} />} />
          <Route path="papierkorb" element={<Papierkorb projekt={projekt} />} />
          <Route
            path="einstellungen"
            element={projekt.verwalten ? <ProjektEinstellungen projekt={projekt} neuLaden={() => void neuLaden()} /> : <Navigate to=".." />}
          />
          {sichtbar.map((m) => m.Seite && <Route key={m.id} path={`${m.id}/*`} element={<m.Seite {...props(m.id)} />} />)}
          <Route path="*" element={<Leer titel="Diesen Bereich gibt es nicht oder du hast keinen Zugriff." />} />
        </Routes>
      </main>
      {sichtbar.map((m) => m.Ueberall && <m.Ueberall key={`ueberall-${m.id}`} {...props(m.id)} />)}
    </div>
  );
}

function Uebersicht({
  projekt,
  sichtbar,
  props,
}: {
  projekt: MeinProjekt;
  sichtbar: WebModul[];
  props(id: WebModul['id']): ModulSeiteProps;
}) {
  // Verlauf sieht nur der Hauptadmin (Marcs Vorgabe) – für alle anderen wird er gar nicht geladen.
  const { istHauptadmin } = useIch();
  const { daten: verlauf } = useDaten(
    () => (istHauptadmin ? api.get<Aktivitaet[]>(`/api/projekte/${projekt.id}/aktivitaet?anzahl=30`) : Promise.resolve(null)),
    [projekt.id, istHauptadmin],
  );
  return (
    <div className="seite">
      {projekt.beschreibung && <p className="beschreibung">{projekt.beschreibung}</p>}
      <div className="raster">
        {sichtbar.filter((m) => m.Karte).map((m) => m.Karte && <m.Karte key={m.id} {...props(m.id)} />)}
      </div>
      {WEB_MODULE.length === 0 ? (
        <Leer titel="Noch keine Teilsysteme installiert" />
      ) : (
        sichtbar.length === 0 && <Leer titel="Noch keine Bereiche freigegeben">Die Projektleitung kann dir Zugriff auf Bereiche geben.</Leer>
      )}
      {istHauptadmin && <Karte titel="Verlauf">
        {verlauf?.length === 0 && <p className="leise">Noch nichts passiert.</p>}
        <ul className="verlauf">
          {verlauf?.map((a) => (
            <li key={a.id}>
              <span className="marke">{a.bereich === 'projekt' ? 'Projekt' : BEREICH_NAMEN[a.bereich]}</span>
              <span>
                <strong>{a.benutzerName ?? 'System'}</strong> {a.text}
              </span>
              <span className="leise klein">{datumZeit(a.erstelltAm)}</span>
            </li>
          ))}
        </ul>
      </Karte>}
    </div>
  );
}
